// PrivacyPolicy.js
import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Divider,
  List,
  ListItem,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';

const campaignImage = `https://${window.location.host}/images/share-image.png`;

const PrivacyPolicy = () => {
  return (
    <Box bg="#f5f3ee" color="#0f393b" py={10} px={6}>
      <Helmet>
        <title>Privacy Policy - PurelyGive</title>
        <meta
          name="description"
          content="Learn how PurelyGive collects, uses, and protects your personal information. Read our comprehensive privacy policy."
        />
        <meta
          name="keywords"
          content="privacy policy, data protection, PurelyGive, personal information, nonprofit fundraising"
        />
        <meta property="og:title" content="PurelyGive Privacy Policy" />
        <meta
          property="og:description"
          content="Understand how PurelyGive handles your personal information on our zero-fee fundraising platform."
        />
        <meta property="og:image" content={campaignImage} />
        <meta
          property="og:url"
          content={`https://${window.location.host}/privacy-policy`}
        />
      </Helmet>
      <VStack spacing={8} maxW="800px" mx="auto" align="stretch" textAlign="left">
        <Heading as="h1" size="2xl" color="#f08453" mb={4}>
          Privacy Policy
        </Heading>

        {/* Privacy Policy */}
        <Box>
          {/* Effective Date */}
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Effective Date: August 13th, 2024
          </Text>
          <Text fontSize="md" mb={4}>
            PurelyGive LLC (“PurelyGive,” “we,” “us,” or “our”) is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our platform, which provides services to verified non-profit organizations (“Non-profits”) and individuals making donations (“Donors”).
          </Text>
          <Text fontSize="md" mb={4}>
            By accessing or using the PurelyGive platform, you agree to this Privacy Policy.
          </Text>

          <br /><Divider />

          {/* Section 1 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            1. Definitions
          </Heading>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem><strong>Non-profits:</strong> Organizations registered and verified as non-profits in the United States.</ListItem>
            <ListItem><strong>Donors:</strong> Individuals making donations to campaigns created by Non-profits.</ListItem>
            <ListItem><strong>Administrators:</strong> Individuals responsible for managing the platform on behalf of PurelyGive.</ListItem>
            <ListItem><strong>Personal Information:</strong> Any information that can be used to identify you, including but not limited to your name, email address, payment information, and any other information collected through our platform.</ListItem>
            <ListItem><strong>Processing:</strong> Any operation performed on personal data, such as collection, use, storage, or disclosure.</ListItem>
          </List>

          {/* Section 2 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            2. Information We Collect
          </Heading>

          {/* 2.1 Personal Information */}
          <Heading as="h3" size="md" mt={2} mb={1}>
            2.1 Personal Information
          </Heading>
          <Text fontSize="md" mb={2}>
            We may collect personal information that you provide directly to us when you register on our platform, make a donation, or interact with our services. This information may include:
          </Text>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem>Name</ListItem>
            <ListItem>Email address</ListItem>
            <ListItem>Postal address</ListItem>
            <ListItem>Phone number</ListItem>
            <ListItem>Payment information (e.g., credit/debit card details, bank account information)</ListItem>
            <ListItem>Tax identification numbers (for Non-profits)</ListItem>
          </List>

          {/* 2.2 Automatically Collected Information */}
          <Heading as="h3" size="md" mt={4} mb={1}>
            2.2 Automatically Collected Information
          </Heading>
          <Text fontSize="md" mb={2}>
            When you use our platform, we may automatically collect certain information about your device and usage, including:
          </Text>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem>IP address</ListItem>
            <ListItem>Browser type</ListItem>
            <ListItem>Device information</ListItem>
            <ListItem>Pages visited and time spent on the platform</ListItem>
            <ListItem>Transaction history</ListItem>
          </List>

          {/* Section 3 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            3. How We Use Your Information
          </Heading>
          <Text fontSize="md" mb={4}>
            We use the information we collect for the following purposes:
          </Text>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem><strong>To Provide Services:</strong> To create and manage your account, process transactions, and deliver services to Non-profits and Donors.</ListItem>
            <ListItem><strong>To Communicate with You:</strong> To send you important updates, newsletters, and other information related to your use of the platform.</ListItem>
            <ListItem><strong>To Improve Our Services:</strong> To analyze usage patterns, improve our platform’s functionality, and develop new features.</ListItem>
            <ListItem><strong>To Comply with Legal Obligations:</strong> To comply with applicable laws, regulations, and contractual obligations, including anti-money laundering and tax reporting requirements.</ListItem>
          </List>

          {/* Section 4 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            4. How We Share Your Information
          </Heading>
          <Text fontSize="md" mb={4}>
            We may share your personal information in the following circumstances:
          </Text>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem><strong>With Payment Processors:</strong> We share payment information with Stripe to process transactions.</ListItem>
            <ListItem><strong>With Financial Partners:</strong> We may share required payout or verification details with Stripe and connected financial institutions to support payment processing and nonprofit payouts.</ListItem>
            <ListItem><strong>With Service Providers:</strong> We may share information with third-party service providers who assist us in operating the platform, such as hosting services, email services, and analytics providers.</ListItem>
            <ListItem><strong>With Legal Authorities:</strong> We may disclose your information to law enforcement, regulatory agencies, or other legal entities if required by law or to protect our rights.</ListItem>
          </List>

          {/* Section 5 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            5. User Rights
          </Heading>
          <Text fontSize="md" mb={4}>
            You have certain rights regarding your personal information, including:
          </Text>
          <List spacing={2} pl={4} styleType="disc">
            <ListItem><strong>Access and Correction:</strong> You can access and update your personal information through your account settings.</ListItem>
            <ListItem><strong>Data Deletion:</strong> You can request the deletion of your personal information by contacting us. We will comply with your request, subject to legal obligations.</ListItem>
            <ListItem><strong>Opt-Out:</strong> You may opt out of receiving marketing communications from us by following the unsubscribe instructions in those emails or by contacting us directly.</ListItem>
            <ListItem><strong>Restriction of Processing:</strong> You may request that we restrict the processing of your personal information if you believe it is inaccurate or if our processing is unlawful.</ListItem>
          </List>

          {/* Section 6 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            6. Data Retention
          </Heading>
          <Text fontSize="md" mb={4}>
            We retain your personal information only as long as necessary to fulfill the purposes for which it was collected or as required by law. For example, we retain transaction information for accounting and tax purposes. Once your personal information is no longer needed, we will securely delete or anonymize it.
          </Text>

          {/* Section 7 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            7. Data Security
          </Heading>
          <Text fontSize="md" mb={4}>
            We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, loss, or misuse. These measures include encryption, secure payment gateways, and regular security assessments. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
          </Text>

          {/* Section 8 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            8. Cookies and Tracking Technologies
          </Heading>
          <Text fontSize="md" mb={4}>
            We use cookies and similar tracking technologies to enhance your experience on our platform, analyze usage patterns, and personalize content. You can control cookie settings through your browser, but disabling cookies may affect your ability to use certain features of the platform.
          </Text>

          {/* Section 9 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            9. Third-Party Links
          </Heading>
          <Text fontSize="md" mb={4}>
            Our platform may contain links to third-party websites or services that are not owned or controlled by PurelyGive. We are not responsible for the privacy practices of these third parties, and we encourage you to review their privacy policies.
          </Text>

          {/* Section 10 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            10. Children’s Privacy
          </Heading>
          <Text fontSize="md" mb={4}>
            Our platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information.
          </Text>

          {/* Section 11 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            11. Changes to This Privacy Policy
          </Heading>
          <Text fontSize="md" mb={4}>
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal obligations. We will notify you of any significant changes by posting the updated policy on our platform and updating the “Effective Date.” Your continued use of the platform after any changes constitutes your acceptance of the revised Privacy Policy.
          </Text>

          {/* Section 12 */}
          <Heading as="h2" size="lg" mt={4} mb={2}>
            12. Contact Us
          </Heading>
          <Text fontSize="md">
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
          </Text>
          <Text fontSize="md" mt={2}>
            PurelyGive LLC
            <br />
            1024 Magna Lane
            <br />
            Indian Trail, NC 28079
            <br />
            <a href="mailto:support@purelygive.com">support@purelygive.com</a>
            <br />
            704-448-3121
          </Text>
        </Box>


        <Divider />

        <Text fontSize="md" mt={4}>
          By using the PurelyGive platform, you acknowledge that you have read,
          understood, and agree to this Privacy Policy.
        </Text>
      </VStack>
    </Box>
  );
};

export default PrivacyPolicy;
